import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventCreate() {
  const navigate = useNavigate();
  const { currentLang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    event_type: 'in_person',
    streaming_url: '',
    use_integrated_streaming: false,
    is_public: true,
    start_date: '',
    end_date: '',
    cover_image_url: '',
    event_language: currentLang || 'it'
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa: JPG, PNG, GIF, WEBP');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`File troppo grande (${(file.size / 1024 / 1024).toFixed(2)}MB). Max 5MB`);
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, cover_image_url: imageUrl }));
      setImagePreview(imageUrl);
      toast.success('Immagine caricata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/events`, formData);
      toast.success('Evento creato!');
      navigate(`/events/${response.data.id}/manage`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="event-create-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/events')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Crea Nuovo Evento</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Titolo Evento *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="event-title-input"
                  className="mt-1"
                  placeholder="Es: Festival Musicale 2025"
                />
              </div>

              <div>
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  data-testid="event-description-input"
                  className="mt-1"
                  placeholder="Descrivi il tuo evento..."
                />
              </div>

              <div>
                <Label htmlFor="event_type">Tipo Evento *</Label>
                <select
                  id="event_type"
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                >
                  <option value="in_person">📍 Dal Vivo</option>
                  <option value="online">🌐 Online</option>
                  <option value="hybrid">🔄 Ibrido (Dal Vivo + Online)</option>
                </select>
              </div>

              <div>
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  data-testid="event-location-input"
                  className="mt-1"
                  placeholder={formData.event_type === 'online' ? 'Online' : 'Es: Piazza Duomo, Milano'}
                />
              </div>

              {/* Streaming Options for Online/Hybrid Events */}
              {(formData.event_type === 'online' || formData.event_type === 'hybrid') && (
                <div className="space-y-4 p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="use_integrated_streaming"
                      checked={formData.use_integrated_streaming}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        use_integrated_streaming: checked,
                        streaming_url: checked ? '' : formData.streaming_url
                      })}
                    />
                    <div className="flex-1">
                      <Label htmlFor="use_integrated_streaming" className="cursor-pointer font-semibold text-blue-900">
                        🎥 Usa Streaming Integrato (Jitsi Meet)
                      </Label>
                      <p className="text-sm text-blue-800 mt-1">
                        Videoconferenza inclusa gratuitamente in ZonePass. Non serve configurare nulla!
                      </p>
                    </div>
                  </div>

                  {!formData.use_integrated_streaming && (
                    <div>
                      <Label htmlFor="streaming_url">Link Streaming Esterno</Label>
                      <Input
                        id="streaming_url"
                        name="streaming_url"
                        value={formData.streaming_url}
                        onChange={handleChange}
                        className="mt-1"
                        placeholder="https://zoom.us/j/... o https://youtube.com/live/..."
                      />
                      <p className="text-sm text-gray-600 mt-1">
                        Usa il tuo link Zoom, Google Meet, YouTube Live, etc.
                      </p>
                    </div>
                  )}

                  {formData.use_integrated_streaming && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800">
                        ✅ <strong>Streaming integrato attivato!</strong> Verrà creata automaticamente 
                        una room videoconferenza accessibile direttamente da ZonePass. 
                        I partecipanti riceveranno il link nel biglietto.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Public/Private Toggle */}
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="is_public"
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                  />
                  <div className="flex-1">
                    <Label htmlFor="is_public" className="flex items-center gap-2 cursor-pointer">
                      {formData.is_public ? (
                        <>
                          <Eye className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-green-700">Evento Pubblico</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-4 h-4 text-orange-600" />
                          <span className="font-semibold text-orange-700">Evento Privato</span>
                        </>
                      )}
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.is_public ? (
                        <>
                          ✅ L'evento sarà <strong>visibile pubblicamente</strong> e apparirà nelle ricerche.
                          Chiunque può trovarlo e acquistare biglietti.
                        </>
                      ) : (
                        <>
                          🔒 L'evento è <strong>privato</strong>. La pagina esiste ma è accessibile
                          <strong> solo tramite link diretto</strong>. Non apparirà nelle ricerche pubbliche.
                          Usa il pulsante "Condividi" per inviare il link agli invitati.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Data Inizio *</Label>
                  <Input
                    id="start_date"
                    name="start_date"
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={handleChange}
                    required
                    data-testid="event-start-date-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">Data Fine *</Label>
                  <Input
                    id="end_date"
                    name="end_date"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={handleChange}
                    required
                    data-testid="event-end-date-input"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Image Upload with Drag & Drop */}
              <div>
                <Label htmlFor="cover_image">Immagine di Copertina</Label>
                
                {/* Upload Zone */}
                <div
                  className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {imagePreview || formData.cover_image_url ? (
                    <div className="space-y-3">
                      <img
                        src={imagePreview || formData.cover_image_url}
                        alt="Preview"
                        className="max-h-48 mx-auto rounded-lg"
                      />
                      <div className="flex gap-2 justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData(prev => ({ ...prev, cover_image_url: '' }));
                          }}
                        >
                          Rimuovi
                        </Button>
                        <label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={uploading}
                            asChild
                          >
                            <span>Cambia Immagine</span>
                          </Button>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileInput}
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="text-sm text-gray-600">
                        <label className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500">
                          <span>Carica un file</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={handleFileInput}
                            disabled={uploading}
                          />
                        </label>
                        <span className="pl-1">o trascina qui</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF, WEBP fino a 5MB
                      </p>
                    </div>
                  )}
                  
                  {uploading && (
                    <div className="mt-4">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Caricamento...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="create-event-submit-button"
                  className="flex-1"
                >
                  {loading ? 'Creazione...' : 'Crea Evento'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/events')}
                  data-testid="cancel-button"
                >
                  Annulla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
